// Barcode/QR camera wrapper for the field app
// (field_barcode_scanner_controller.js, field_ranging_scanner_controller.js).
//
// Card #4861 -- html5-qrcode is loaded lazily rather than bundled statically.
// Most field sessions never open the barcode scanner sheet, so the field
// bundle shouldn't carry its weight on every install just to reach the ~100
// controllers it actually needs. Same lazy-chunk pattern as
// field_image_compressor.js's heic_decoder.js (card #3363): the library is
// its own esbuild entry (field_barcode_scanner_lib.js), loaded via a bare
// import(url) resolved off <meta name="barcode-scanner-url">
// (field.html.erb), with its own load timeout so a wedged or missing chunk
// fails fast with a clear status rather than leaving the "Point camera at a
// barcode" sheet spinning forever -- both FieldCamera callers already catch
// start()'s rejection and set a "Camera unavailable" status, so that failure
// mode was already handled; this just makes sure it actually fires instead
// of hanging. The chunk itself is pre-cached by the service worker (see
// field_offline_manifest.json.erb's "assets" list) so a Tasker who never
// opened the scanner while online still has it available in a no-signal store.

let scannerModule = null

function _resolveScannerUrl() {
  return document.querySelector('meta[name="barcode-scanner-url"]')?.content || null
}

// One shared import per page, same caching shape as field_image_compressor.js's
// _loadHeicDecoder. A failed load is forgotten so the next scan attempt retries
// rather than replaying the same rejected promise forever.
function _loadScanner() {
  const url = _resolveScannerUrl()
  if (!url) return Promise.reject(new Error("Barcode scanner URL not configured"))
  if (!scannerModule || scannerModule.url !== url) {
    const promise = import(url).catch(error => {
      if (scannerModule?.promise === promise) scannerModule = null
      throw error
    })
    scannerModule = { url, promise }
  }
  return scannerModule.promise
}

// Generous but bounded: the chunk is small (html5-qrcode, no model weights
// like the HEIC decoder's ~3MB), but a dead-zone store with no cached copy
// yet should still fail with a clear status rather than hang the sheet.
const SCANNER_LOAD_TIMEOUT_MS = 20_000

function _withTimeout(promise, ms, message) {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

export class FieldCamera {
  constructor(elementId) {
    this._elementId = elementId
    this._instance = null
    this._facingMode = "environment"
  }

  async start(onScan) {
    if (this._instance) return
    const { Html5Qrcode } = await _withTimeout(
      _loadScanner(),
      SCANNER_LOAD_TIMEOUT_MS,
      "Barcode scanner failed to load"
    )
    this._instance = new Html5Qrcode(this._elementId)
    await this._instance.start(
      { facingMode: this._facingMode },
      { fps: 10, qrbox: { width: 250, height: 150 } },
      onScan,
      () => {}
    )
  }

  async stop() {
    if (!this._instance) return
    try { await this._instance.stop() } catch (_) {}
    this._instance = null
  }

  async flip() {
    await this.stop()
    this._facingMode = this._facingMode === "environment" ? "user" : "environment"
  }

  get facingMode() { return this._facingMode }
}
